import React, { useEffect, useState } from 'react';
import { Collapse, Empty, Image, Space, Spin, Tag, Typography } from 'antd';
import AdaptiveTable from './AdaptiveTable';
import { shipmentApi, type ManualMeasurementItem, type ManualMeasurementRecordDTO } from '../api/kfc';

const statusMeta: Record<ManualMeasurementRecordDTO['status'], { text: string; color: string }> = {
  PARSING: { text: '解析中', color: 'processing' },
  PARSED: { text: '待确认', color: 'orange' },
  CONFIRMED: { text: '已确认（生效）', color: 'green' },
  SUPERSEDED: { text: '已被覆盖', color: 'default' },
};

const fmtTime = (v?: string) => (v ? v.replace('T', ' ').slice(0, 19) : '-');

const RecordImage: React.FC<{ shipmentId: string; recordId: string; index: number; fileName: string }> = ({
  shipmentId,
  recordId,
  index,
  fileName,
}) => {
  const [url, setUrl] = useState<string>();
  useEffect(() => {
    let objectUrl: string | undefined;
    let cancelled = false;
    shipmentApi
      .getManualMeasurementImage(shipmentId, recordId, index)
      .then((res) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(res.data as Blob);
        setUrl(objectUrl);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [shipmentId, recordId, index]);
  if (!url) {
    return (
      <div style={{ width: 72, height: 72, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fafafa' }}>
        <Spin size="small" />
      </div>
    );
  }
  return <Image src={url} alt={fileName} width={72} height={72} style={{ objectFit: 'cover', borderRadius: 4 }} />;
};

const itemColumns = [
  { title: '箱号', dataIndex: 'cartonId', render: (v?: string | null) => v || '-' },
  { title: '长', dataIndex: 'lengthCm' },
  { title: '宽', dataIndex: 'widthCm' },
  { title: '高', dataIndex: 'heightCm' },
  { title: '毛重', dataIndex: 'grossWeightKg' },
  { title: '体积重', dataIndex: 'volumeWeightKg', render: (v?: number | null) => v ?? '-' },
  { title: '体积', dataIndex: 'volumeCbm', render: (v?: number | null) => v ?? '-' },
];

const RecordDetail: React.FC<{ record: ManualMeasurementRecordDTO }> = ({ record }) => {
  const items: ManualMeasurementItem[] = record.confirmedItems ?? record.parsedItems ?? [];
  return (
    <div style={{ fontSize: 12 }}>
      {record.text && (
        <Typography.Paragraph
          style={{ whiteSpace: 'pre-wrap', background: '#fafafa', padding: 8, marginBottom: 8, fontSize: 12 }}
          ellipsis={{ rows: 4, expandable: true, symbol: '展开' }}
        >
          {record.text}
        </Typography.Paragraph>
      )}
      {!!record.images?.length && (
        <Image.PreviewGroup>
          <Space wrap style={{ marginBottom: 8 }}>
            {record.images.map((img) => (
              <RecordImage
                key={img.index}
                shipmentId={record.shipmentId}
                recordId={record.id}
                index={img.index}
                fileName={img.fileName}
              />
            ))}
          </Space>
        </Image.PreviewGroup>
      )}
      {record.parseError && (
        <div style={{ color: '#cf1322', marginBottom: 8 }}>解析失败：{record.parseError}</div>
      )}
      {!!record.conflicts?.length && (
        <div style={{ color: '#d46b08', marginBottom: 8 }}>
          冲突：
          {record.conflicts.map((c) => `${c.field}（文字 ${c.textValue || '-'} / 图片 ${c.imageValue || '-'}）`).join('；')}
        </div>
      )}
      {items.length > 0 && (
        <AdaptiveTable
          size="small"
          rowKey={(_, i) => String(i)}
          pagination={false}
          dataSource={items}
          columns={itemColumns}
          scroll={{ y: 220 }}
        />
      )}
      <div style={{ color: '#888', marginTop: 6 }}>
        {record.llmModel ? `模型 ${record.llmModel}` : ''}
        {record.promptTokens != null ? ` · tokens ${record.promptTokens}/${record.completionTokens ?? 0}` : ''}
        {record.parseDurationMs != null ? ` · 耗时 ${(record.parseDurationMs / 1000).toFixed(1)}s` : ''}
        {record.manualOnly ? ' · 手工填写' : ''}
      </div>
    </div>
  );
};

interface ManualMeasurementHistoryProps {
  shipmentId: string;
  /** 变化时刷新（如确认成功后） */
  refreshKey?: number;
}

/** 运单详情「上传记录」：展开时加载该运单的手动测量上传历史 */
const ManualMeasurementHistory: React.FC<ManualMeasurementHistoryProps> = ({ shipmentId, refreshKey }) => {
  const [expanded, setExpanded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState<ManualMeasurementRecordDTO[]>([]);

  useEffect(() => {
    if (!expanded) return;
    setLoading(true);
    shipmentApi
      .listManualMeasurements(shipmentId)
      .then((res) => setRecords(res.data || []))
      .catch(() => setRecords([]))
      .finally(() => setLoading(false));
  }, [expanded, shipmentId, refreshKey]);

  return (
    <Collapse
      size="small"
      ghost
      style={{ marginTop: 12 }}
      onChange={(keys) => setExpanded((Array.isArray(keys) ? keys : [keys]).includes('history'))}
      items={[
        {
          key: 'history',
          label: '上传记录',
          children: (
            <Spin spinning={loading}>
              {records.length === 0 && !loading ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无上传记录" />
              ) : (
                <AdaptiveTable<ManualMeasurementRecordDTO>
                  size="small"
                  rowKey="id"
                  pagination={false}
                  dataSource={records}
                  expandable={{ expandedRowRender: (r) => <RecordDetail record={r} /> }}
                  columns={[
                    { title: '上传时间', dataIndex: 'createdAt', width: 160, render: fmtTime },
                    { title: '上传人', dataIndex: 'createdByName', width: 100, render: (v, r) => v || r.createdBy || '-' },
                    {
                      title: '状态',
                      dataIndex: 'status',
                      width: 120,
                      render: (v: ManualMeasurementRecordDTO['status'], r) => (
                        <Space size={4}>
                          <Tag color={statusMeta[v]?.color}>{statusMeta[v]?.text ?? v}</Tag>
                          {r.parseError && <Tag color="red">解析失败</Tag>}
                        </Space>
                      ),
                    },
                    {
                      title: '箱数',
                      width: 60,
                      render: (_, r) => (r.confirmedItems ?? r.parsedItems ?? []).length,
                    },
                    { title: '确认时间', dataIndex: 'confirmedAt', width: 160, render: fmtTime },
                    { title: '确认人', dataIndex: 'confirmedByName', width: 100, render: (v, r) => v || r.confirmedBy || '-' },
                  ]}
                />
              )}
            </Spin>
          ),
        },
      ]}
    />
  );
};

export default ManualMeasurementHistory;
